import { ReviewStayModel } from "../../../interfaces/models/reviewStayModel";
import { ListingInterface } from "../viewListing";
import { getUserCurrencyLabel, getUserCurrencySymbol } from "../../../utils/userCurrencyUtils";
import { UserModel } from "../../../interfaces/models/userModel";
import {
    ViewListingReviewWrapper,
    ViewListingHighlightTitle,
    ViewListingReviewContentSubtitle,
    ViewListingSectionWrapper,
    ViewListingHighlightTextWrapper,
    ViewListingHighlightSubtitle,
    ViewListingHighlightIconWrapper,
} from "../viewListingStyling";

interface ViewListingHighlightsProps {
    listing: ListingInterface;
    reviews: ReviewStayModel[];
    userModel: UserModel | null;
}

interface AmenityHighlight {
    icon: string;
    title: string;
    subtitle: string;
}

type AmenityKey = 'Pool' | 'Gym' | 'Wifi' | 'Beach access' | 'Air conditioning' | 'BBQ' | 'Parking' | 'Pet friendly';

// Map of amenities to their display info
const AMENITY_HIGHLIGHTS: Record<AmenityKey, AmenityHighlight> = {
    'Pool': {
        icon: '🏊‍♂️',
        title: 'Pool access',
        subtitle: 'This property has a pool for guests to enjoy.'
    },
    'Gym': {
        icon: '💪',
        title: 'Fitness center',
        subtitle: 'Stay fit with access to exercise equipment.'
    },
    'Wifi': {
        icon: '📶',
        title: 'Fast wifi',
        subtitle: 'Stay connected with high-speed internet.'
    },
    'Beach access': {
        icon: '🏖️',
        title: 'Beach nearby',
        subtitle: 'Just a short walk to the beach.'
    },
    'Air conditioning': {
        icon: '❄️',
        title: 'Air conditioning',
        subtitle: 'Stay cool with climate control.'
    },
    'BBQ': {
        icon: '🍖',
        title: 'BBQ facilities',
        subtitle: 'Perfect for outdoor dining.'
    },
    'Parking': {
        icon: '🅿️',
        title: 'Free parking',
        subtitle: 'Convenient on-site parking available.'
    },
    'Pet friendly': {
        icon: '🐾',
        title: 'Pet friendly',
        subtitle: 'Bring your furry friends along.'
    }
};

// Priority order for amenities (most important first)
const AMENITY_PRIORITY: AmenityKey[] = [
    'Pool',
    'Beach access',
    'Gym',
    'Pet friendly',
    'BBQ',
    'Parking',
    'Air conditioning',
    'Wifi'
];

export default function ViewListingHighlights({ listing, reviews, userModel }: ViewListingHighlightsProps) {
    const currencyLabel = getUserCurrencyLabel(userModel);
    const currencySymbol = getUserCurrencySymbol(userModel);
    
    // Get the top 3 amenities based on priority
    const getTopAmenities = (): AmenityKey[] => {
        if (!listing?.amenities) return [];
        
        const availableAmenities = listing.amenities.filter((amenity): amenity is AmenityKey => 
            AMENITY_PRIORITY.includes(amenity as AmenityKey)
        );
        
        // Sort by priority and take first 3
        return availableAmenities
            .sort((a, b) => 
                AMENITY_PRIORITY.indexOf(a) - AMENITY_PRIORITY.indexOf(b)
            )
            .slice(0, 3);
    };

    const topAmenities = getTopAmenities();

    // Function to render the third highlight based on priority
    const renderThirdHighlight = () => {
        // First priority: Free cancellation if available
        if (listing?.cancellationPolicy?.includes('flexible')) {
            return (
                <ViewListingSectionWrapper>
                    <ViewListingHighlightIconWrapper>
                    🗓️
                    </ViewListingHighlightIconWrapper>
                    <ViewListingHighlightTextWrapper>
                        <ViewListingHighlightTitle>Free cancellation before check-in</ViewListingHighlightTitle>
                        <ViewListingHighlightSubtitle>Cancel for free up to 24 hours before check-in.</ViewListingHighlightSubtitle>
                    </ViewListingHighlightTextWrapper>
                </ViewListingSectionWrapper>
            );
        }
        
        // Second priority: Amenity if available
        if (topAmenities[1]) {
            return (
                <ViewListingSectionWrapper>
                    <ViewListingHighlightIconWrapper>
                        {AMENITY_HIGHLIGHTS[topAmenities[1]].icon}
                    </ViewListingHighlightIconWrapper>
                    <ViewListingHighlightTextWrapper>
                        <ViewListingHighlightTitle>
                            {AMENITY_HIGHLIGHTS[topAmenities[1]].title}
                        </ViewListingHighlightTitle>
                        <ViewListingHighlightSubtitle>
                            {AMENITY_HIGHLIGHTS[topAmenities[1]].subtitle}
                        </ViewListingHighlightSubtitle>
                    </ViewListingHighlightTextWrapper>
                </ViewListingSectionWrapper>
            );
        }

        // Fallback: Experienced host
        return (
            <ViewListingSectionWrapper>
                <ViewListingHighlightIconWrapper>
                    🕒
                </ViewListingHighlightIconWrapper>
                <ViewListingHighlightTextWrapper>
                    <ViewListingHighlightTitle>Experienced host</ViewListingHighlightTitle>
                    <ViewListingHighlightSubtitle>Your host has been hosting for a long time.</ViewListingHighlightSubtitle>
                </ViewListingHighlightTextWrapper>
            </ViewListingSectionWrapper>
        );
    };

    return (
        <ViewListingReviewWrapper>
            {/* Welcome offer highlight - Always first */}
            <ViewListingSectionWrapper>
                <ViewListingHighlightIconWrapper>
                    ✨
                </ViewListingHighlightIconWrapper>
                <ViewListingHighlightTextWrapper>
                    <ViewListingHighlightTitle>{currencySymbol}25 {currencyLabel} off your first stay</ViewListingHighlightTitle>
                    <ViewListingHighlightSubtitle>Use the code WELCOME25 at checkout.</ViewListingHighlightSubtitle>
                </ViewListingHighlightTextWrapper>
            </ViewListingSectionWrapper>

            {/* Top amenity highlight or verification as fallback */}
            {topAmenities[0] ? (
                <ViewListingSectionWrapper>
                    <ViewListingHighlightIconWrapper>
                        {AMENITY_HIGHLIGHTS[topAmenities[0]].icon}
                    </ViewListingHighlightIconWrapper>
                    <ViewListingHighlightTextWrapper>
                        <ViewListingHighlightTitle>
                            {AMENITY_HIGHLIGHTS[topAmenities[0]].title}
                        </ViewListingHighlightTitle>
                        <ViewListingHighlightSubtitle>
                            {AMENITY_HIGHLIGHTS[topAmenities[0]].subtitle}
                        </ViewListingHighlightSubtitle>
                    </ViewListingHighlightTextWrapper>
                </ViewListingSectionWrapper>
            ) : (
                <ViewListingSectionWrapper>
                    <ViewListingHighlightIconWrapper>
                        🔒
                    </ViewListingHighlightIconWrapper>
                    <ViewListingHighlightTextWrapper>
                        <ViewListingHighlightTitle>This property is verified</ViewListingHighlightTitle>
                        <ViewListingHighlightSubtitle>We have verified this property's details.</ViewListingHighlightSubtitle>
                    </ViewListingHighlightTextWrapper>
                </ViewListingSectionWrapper>
            )}

            {/* Third highlight with priority order */}
            {renderThirdHighlight()}
        </ViewListingReviewWrapper>
    );
}